"use client";

import React, { useEffect, useRef } from "react";
import Link from "next/link";
import { gsap } from "gsap";
import { legalData } from "@/data/legalData";

interface LegalLayoutProps {
  activeTab: "terms" | "privacy" | "disclosures";
}

export function LegalLayout({ activeTab }: LegalLayoutProps) {
  const { companyName, contactEmail } = legalData;

  const containerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const sectionsRef = useRef<HTMLElement[]>([]);
  const footerRef = useRef<HTMLDivElement>(null);

  const navLinks = [
    { href: "/terms", key: "terms", label: "Terms & Conditions" },
    { href: "/privacy", key: "privacy", label: "Privacy Policy" },
    { href: "/disclosures", key: "disclosures", label: "Disclosures" },
  ];

  const currentData = legalData[activeTab];

  sectionsRef.current = [];
  const addToSectionsRef = (el: HTMLElement | null) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Initial page load animation
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.fromTo(
        sidebarRef.current,
        { opacity: 0, x: -30 },
        { opacity: 1, x: 0, duration: 0.8 }
      )
        .fromTo(
          mainRef.current,
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: 0.9 },
          "-=0.5"
        )
        .fromTo(
          headerRef.current,
          { opacity: 0, y: -20 },
          { opacity: 1, y: 0, duration: 0.7 },
          "-=0.6"
        )
        .fromTo(
          sectionsRef.current,
          { opacity: 0, y: 25, scale: 0.98 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            stagger: 0.12,
            ease: "power2.out",
          },
          "-=0.4"
        )
        .fromTo(
          footerRef.current,
          { opacity: 0, y: 15 },
          { opacity: 1, y: 0, duration: 0.5 },
          "-=0.2"
        );
    }, containerRef);

    return () => ctx.revert();
  }, [activeTab]);

  const handleNavHover = (
    e: React.MouseEvent<HTMLAnchorElement>,
    isEnter: boolean
  ) => {
    const target = e.currentTarget;
    if (!target.classList.contains("active-tab")) {
      gsap.to(target, {
        x: isEnter ? 6 : 0,
        duration: 0.25,
        ease: "power2.out",
      });
    }
  };

  const handleSectionHover = (
    e: React.MouseEvent<HTMLElement>,
    isEnter: boolean
  ) => {
    gsap.to(e.currentTarget, {
      x: isEnter ? 4 : 0,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen flex flex-col bg-gradient-to-br from-stone-50 via-white to-stone-100 text-stone-800 font-sans"
    >
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-stone-200/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-stone-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-stone-100/40 rounded-full blur-3xl" />
      </div>

      {/* Main Content Area */}
      <div className="relative flex-1 mx-auto max-w-5xl w-full px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
          {/* Sidebar Navigation */}
          <nav ref={sidebarRef} className="md:col-span-1">
            <div className="flex flex-row space-x-2 border-b border-stone-200/60 pb-4 md:flex-col md:space-x-0 md:space-y-2 md:border-b-0 sticky top-8">
              <div className="hidden md:block mb-4 px-4">
                <p className="text-xs font-semibold tracking-widest text-stone-400 uppercase">
                  Legal
                </p>
              </div>
              {navLinks.map((link) => (
                <Link
                  key={link.key}
                  href={link.href}
                  onMouseEnter={(e) => handleNavHover(e, true)}
                  onMouseLeave={(e) => handleNavHover(e, false)}
                  className={`flex-1 rounded-xl px-4 py-3 text-left text-sm font-medium transition-all duration-300 ${
                    activeTab === link.key
                      ? "active-tab bg-stone-900 text-white shadow-lg shadow-stone-900/20 ring-1 ring-stone-700"
                      : "text-stone-500 hover:bg-white hover:text-stone-900 hover:shadow-md hover:shadow-stone-200/60"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {activeTab === link.key && (
                      <span className="w-1.5 h-1.5 rounded-full bg-stone-400 inline-block" />
                    )}
                    {link.label}
                  </span>
                </Link>
              ))}

              {/* Last updated info */}
              <div className="hidden md:block mt-6 px-4">
                <p className="text-xs text-stone-400">
                  Last updated
                  <br />
                  <span className="font-medium text-stone-500">
                    {legalData.lastUpdated}
                  </span>
                </p>
              </div>
            </div>
          </nav>

          {/* Legal Documents */}
          <main ref={mainRef} className="md:col-span-3">
            <div className="rounded-2xl border border-stone-200/60 bg-white/80 backdrop-blur-sm p-8 shadow-xl shadow-stone-200/40 sm:p-12 ring-1 ring-stone-100">
              {/* Article Header */}
              <header
                ref={headerRef}
                className="border-b border-stone-100 pb-6 mb-8"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-8 w-1 bg-stone-900 rounded-full" />
                      <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
                        {currentData.title}
                      </h2>
                    </div>
                    <p className="text-sm text-stone-500 ml-4">
                      {currentData.description}
                    </p>
                  </div>
                  <span className="hidden sm:flex items-center gap-1.5 text-xs text-stone-400 bg-stone-50 px-3 py-1.5 rounded-full border border-stone-100 mt-1">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    Current
                  </span>
                </div>
              </header>

              {/* Sections */}
              <article className="space-y-1">
                {currentData.sections.map((section, index) => (
                  <section
                    key={section.id}
                    ref={addToSectionsRef}
                    onMouseEnter={(e) => handleSectionHover(e, true)}
                    onMouseLeave={(e) => handleSectionHover(e, false)}
                    className="group p-5 rounded-xl hover:bg-stone-50/80 transition-colors duration-300 cursor-default"
                  >
                    <div className="flex items-start gap-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-stone-100 group-hover:bg-stone-200 text-stone-500 rounded-lg flex items-center justify-center text-xs font-bold transition-colors duration-300">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-stone-900 mb-2 group-hover:text-stone-700 transition-colors">
                          {section.title}
                        </h3>
                        <p className="text-sm leading-relaxed text-stone-500 group-hover:text-stone-600 transition-colors">
                          {section.content.replace("{companyName}", companyName)}
                        </p>
                      </div>
                    </div>
                  </section>
                ))}
              </article>

              {/* Divider */}
              <div className="my-8 border-t border-stone-100" />

              {/* Direct Support Note */}
              <div
                ref={footerRef}
                className="rounded-xl border border-stone-100 bg-gradient-to-r from-stone-50 to-stone-100/50 p-5"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-stone-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-900 mb-0.5">
                      Need legal assistance?
                    </p>
                    <p className="text-xs text-stone-500 leading-relaxed">
                      Have specific compliance or legal questions? Reach out to
                      our legal team directly at{" "}
                      <a
                        href={`mailto:${contactEmail}`}
                        className="font-semibold text-stone-900 underline underline-offset-2 hover:text-stone-600 transition-colors"
                      >
                        {contactEmail}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}